import React, { useState } from 'react';
import { SparklesIcon, ImageIcon, Undo2Icon, Redo2Icon, RotateCcwIcon, UploadIcon, LockIcon, PaletteIcon, LayersIcon, TypeIcon, MousePointerClickIcon, PanelTopIcon, ChevronDownIcon } from 'lucide-react';
import { StepHeading, Field, TextArea, AiButton } from '../SettingsUI';
import { ShopConfig, ShopTheme, ThemePresetId, BackgroundStyle, BackdropStyle, ButtonShape, LogoDisplay, HeroStyle, HeroEmphasis, ChatBackground } from '../../../data/shopConfig';
import { THEME_PRESETS, proposeBrandTheme, resolveTheme, isLowContrastForWhite, rgba } from '../../../utils/theme';
import { useBusiness } from '../../BusinessContext';
import { UpgradePlanModal } from '../../UpgradePlanModal';
import { DesignSection, ControlLabel, VisualChoice, RangeControl, SwitchRow, ColorRow } from '../design/DesignControls';
interface Props {
  config: ShopConfig;
  update: (patch: Partial<ShopConfig>) => void;
  /** Theme history, owned by the settings shell so it survives section changes. */
  canUndo: boolean;
  canRedo: boolean;
  onUndo: () => void;
  onRedo: () => void;
  onResetTheme: () => void;
  /** Rendered inside the Shop Studio panel — tighter, no duplicate header. */
  compact?: boolean;
}
type SectionId = 'logo' | 'colours' | 'background' | 'welcome' | 'buttons' | 'topbar';
const BACKDROPS: {
  id: BackdropStyle;
  label: string;
  thumb: (p: string) => string;
}[] = [{
  id: 'plain',
  label: 'Plain',
  thumb: p => rgba(p, 0.05)
}, {
  id: 'dots',
  label: 'Dots',
  thumb: p => `radial-gradient(circle at 1px 1px, ${rgba(p, 0.3)} 1px, transparent 0) 0 0 / 8px 8px, ${rgba(p, 0.04)}`
}, {
  id: 'grid',
  label: 'Grid',
  thumb: p => `linear-gradient(to right, ${rgba(p, 0.2)} 1px, transparent 1px) 0 0 / 12px 12px, linear-gradient(to bottom, ${rgba(p, 0.2)} 1px, transparent 1px) 0 0 / 12px 12px, ${rgba(p, 0.04)}`
}, {
  id: 'lines',
  label: 'Diagonal',
  thumb: p => `repeating-linear-gradient(135deg, ${rgba(p, 0.2)} 0 1px, transparent 1px 7px), ${rgba(p, 0.04)}`
}, {
  id: 'rings',
  label: 'Rings',
  thumb: p => `repeating-radial-gradient(circle at 50% -10%, transparent 0 10px, ${rgba(p, 0.22)} 10px 11px), ${rgba(p, 0.04)}`
}];
const SHAPE_RADIUS: Record<ButtonShape, string> = {
  rounded: '999px',
  soft: '10px',
  square: '3px'
};
export function ShopDesign({
  config,
  update,
  canUndo,
  canRedo,
  onUndo,
  onRedo,
  onResetTheme,
  compact
}: Props) {
  const [open, setOpen] = useState<SectionId | null>(null);
  const [proposal, setProposal] = useState<ReturnType<typeof proposeBrandTheme> | null>(null);
  const [writing, setWriting] = useState(false);
  const [upgrade, setUpgrade] = useState(false);
  const [moreWelcome, setMoreWelcome] = useState(false);
  const {
    currentPlanId,
    subscribePackage
  } = useBusiness();
  const canRemoveTalab = currentPlanId === 'Scale';
  const t = resolveTheme(config.theme);
  const p = config.theme.primary;
  const toggle = (id: SectionId) => setOpen(o => o === id ? null : id);
  const setTheme = (patch: Partial<ShopTheme>) => update({
    theme: {
      ...config.theme,
      ...patch
    }
  });
  const setHero = (patch: Partial<ShopConfig['hero']>) => update({
    hero: {
      ...config.hero,
      ...patch
    }
  });
  const applyPreset = (id: ThemePresetId) => {
    const preset = THEME_PRESETS.find(x => x.id === id);
    if (!preset) return;
    setTheme({
      preset: id,
      ...preset.apply({
        primary: config.theme.primary,
        accent: config.theme.accent
      })
    });
  };
  const generateHeroCopy = () => {
    setWriting(true);
    window.setTimeout(() => {
      setHero({
        headline: `Ask ${config.publicName} Anything`,
        subtitle: `Find, compare and rent the right ${config.businessType.toLowerCase().includes('vehicle') ? 'vehicle' : 'option'} with AI — with delivery, support and flexible terms across the UAE.`
      });
      setWriting(false);
    }, 900);
  };
  const words = config.hero.headline.trim().split(/\s+/);
  const lastWord = words.length > 1 ? words[words.length - 1] : 'Anything';
  const presetName = THEME_PRESETS.find(x => x.id === config.theme.preset)?.name ?? 'Custom';
  const backdropName = BACKDROPS.find(b => b.id === config.theme.backdrop)?.label ?? 'Plain';
  const shapeName = {
    rounded: 'Rounded',
    soft: 'Soft',
    square: 'Square'
  }[config.theme.buttonShape];
  const Dot = ({
    c
  }: {
    c: string;
  }) => <span className="w-3 h-3 rounded-full ring-1 ring-black/10 flex-shrink-0" style={{
    background: c
  }} data-id="element-4113" data-mp="2b8gqg1l6thqi" />;
  return <div className="space-y-3" data-id="element-4114" data-mp="2kzukhtuddpc9">
      {compact ? <p className="text-[13px] text-slate-500 leading-relaxed pb-1" data-id="element-4115" data-mp="2l27b0ve7180x">
          Open any part to change it — your shop updates as you go.
        </p> : <div className="flex flex-wrap items-end justify-between gap-3" data-id="element-4116" data-mp="2uzlg5yuo2adx">
          <StepHeading title="Shop Design" desc="Open any part to change it — your shop updates as you go." data-id="element-4117" data-mp="2yzdteov094pm" />
          <div className="flex items-center gap-1.5 pb-6" data-id="element-4118" data-mp="ndv4j0h19oum">
            <button onClick={onUndo} disabled={!canUndo} className="p-2 rounded-lg border border-slate-200 text-slate-500 hover:text-primary-indigo hover:border-primary-indigo/40 disabled:opacity-40 transition-colors duration-150 ease-out" aria-label="Undo" data-id="element-4119" data-mp="jap100lb2bru">
              <Undo2Icon className="w-4 h-4" data-id="element-4120" data-mp="16zztkusi4zuw" />
            </button>
            <button onClick={onRedo} disabled={!canRedo} className="p-2 rounded-lg border border-slate-200 text-slate-500 hover:text-primary-indigo hover:border-primary-indigo/40 disabled:opacity-40 transition-colors duration-150 ease-out" aria-label="Redo" data-id="element-4121" data-mp="120wqvgzzv5q3">
              <Redo2Icon className="w-4 h-4" data-id="element-4122" data-mp="1prskmjo0gazz" />
            </button>
          </div>
        </div>}

      {/* 1 — Logo */}
      <DesignSection icon={ImageIcon} title="Logo" summary={config.logo ? 'Your logo is added' : 'Not added — your name shows instead'} open={open === 'logo'} onToggle={() => toggle('logo')} data-id="element-4123" data-mp="1ky7mmrmf3h0s">
        <div className="flex items-center gap-4" data-id="element-4124" data-mp="35iux3nzk8ts7">
          <div className="w-[72px] h-[72px] rounded-2xl bg-slate-50 ring-1 ring-slate-200 flex items-center justify-center overflow-hidden flex-shrink-0" data-id="element-4125" data-mp="35i31we9vky2g">
            {config.logo ? <img src={config.logo} alt="" className="w-full h-full object-contain p-2" data-id="element-4126" data-mp="2vilxs1a9us8l" /> : <ImageIcon className="w-6 h-6 text-slate-300" data-id="element-4127" data-mp="30kjn9d99mj9z" />}
          </div>
          <div className="flex flex-col items-start gap-1.5" data-id="element-4128" data-mp="2ml1vcp0oapwe">
            <label className="inline-flex items-center gap-2 px-3.5 py-2 bg-white ring-1 ring-slate-200 rounded-xl text-[13px] font-semibold text-dark-navy hover:ring-primary-indigo/50 cursor-pointer transition-shadow duration-150 ease-out" data-id="element-4129" data-mp="2rhs7x2d9r3d2">
              <UploadIcon className="w-4 h-4" data-id="element-4130" data-mp="pqfdx8cuafvs" />
              {config.logo ? 'Replace' : 'Upload logo'}
              <input type="file" accept="image/*" className="hidden" onChange={e => {
              const file = e.target.files?.[0];
              if (file) update({
                logo: URL.createObjectURL(file)
              });
            }} data-id="element-4131" data-mp="zt182l7840ug" />
            </label>
            {config.logo && <button onClick={() => update({
            logo: ''
          })} className="text-[12px] text-slate-400 hover:text-error transition-colors duration-150 ease-out" data-id="element-4132" data-mp="1ikkaoqxc5cvy">
                Remove
              </button>}
          </div>
        </div>
        <div data-id="element-4133" data-mp="1nkggmvplfee5">
          <ControlLabel data-id="element-4134" data-mp="1nm1a0i068raf">Show beside the logo</ControlLabel>
          <VisualChoice<LogoDisplay> columns={2} value={config.hero.logoDisplay} onChange={logoDisplay => setHero({
          logoDisplay
        })} options={[{
          id: 'logo',
          label: 'Logo only',
          preview: <span className="flex h-12 items-center justify-center bg-slate-50" data-id="element-4136" data-mp="mqgg4z81xd52">
                    <span className="w-6 h-6 rounded-md" style={{
              background: p
            }} data-id="element-4137" data-mp="rhezk7oc6rcj" />
                  </span>
        }, {
          id: 'logo-name',
          label: 'Logo + name',
          preview: <span className="flex h-12 items-center justify-center gap-1.5 bg-slate-50" data-id="element-4138" data-mp="3uzq7wych5rmn">
                    <span className="w-6 h-6 rounded-md" style={{
              background: p
            }} data-id="element-4139" data-mp="3sdyx6lqwkht" />
                    <span className="w-10 h-2 rounded-full bg-slate-300" data-id="element-4140" data-mp="7so5akdg9cx7" />
                  </span>
        }]} data-id="element-4135" data-mp="3tdeqmjxusw9u" />
        </div>
      </DesignSection>

      {/* 2 — Colours */}
      <DesignSection icon={PaletteIcon} title="Colours" summary={<>
            <Dot c={config.theme.primary} data-id="element-4142" data-mp="1o5uu6i6azqwi" />
            <Dot c={config.theme.accent} data-id="element-4143" data-mp="1yc5smj7l0o18" />
            <span className="ml-0.5" data-id="element-4144" data-mp="1yb4icgye45kk">{presetName}</span>
          </>} open={open === 'colours'} onToggle={() => toggle('colours')} data-id="element-4141" data-mp="1j82z5fad3oo3">
        {/* The fastest route: let Talab pick */}
        <div className="rounded-xl bg-primary-indigo/[0.04] ring-1 ring-primary-indigo/10 p-3.5" data-id="element-4145" data-mp="1ok05bms9yusd">
          {proposal ? <>
              <p className="text-[13px] font-semibold text-dark-navy" data-id="element-4146" data-mp="1flo30ni1q8k3">
                Colours found in your logo
              </p>
              <div className="flex items-center gap-2 mt-2.5" data-id="element-4147" data-mp="1ala5w0hzgj0u">
                {[proposal.primary, proposal.accent, proposal.background].map(c => <span key={c} className="w-8 h-8 rounded-lg ring-1 ring-black/5" style={{
              background: c
            }} data-id="element-4148" data-mp="11lddpowdx211" />)}
                <div className="ml-auto flex items-center gap-2" data-id="element-4149" data-mp="2vzo1du18tq58">
                  <button onClick={() => setProposal(null)} className="px-2.5 py-2 text-[12.5px] font-semibold text-slate-500 hover:text-dark-navy transition-colors duration-150 ease-out" data-id="element-4150" data-mp="2vxa6pmah6fy7">
                    Cancel
                  </button>
                  <button onClick={() => {
                setTheme({
                  preset: 'auto',
                  primary: proposal.primary,
                  accent: proposal.accent,
                  background: proposal.background,
                  backgroundStyle: 'tint'
                });
                setProposal(null);
              }} className="px-3.5 py-2 rounded-lg bg-primary-indigo text-white text-[12.5px] font-semibold hover:bg-indigo-700 transition-colors duration-150 ease-out" data-id="element-4151" data-mp="2m7hrtdayttbf">
                    Use these
                  </button>
                </div>
              </div>
            </> : <button onClick={() => setProposal(proposeBrandTheme('logo'))} className="w-full flex items-center gap-3 text-left" data-id="element-4152" data-mp="3hifna55p9k8z">
              <SparklesIcon className="w-4 h-4 text-primary-indigo flex-shrink-0" data-id="element-4153" data-mp="38hzld04lul68" />
              <span className="flex-1" data-id="element-4154" data-mp="38g6epktzs0y8">
                <span className="block text-[13px] font-semibold text-dark-navy" data-id="element-4155" data-mp="3citec7wwmq5z">
                  Match my logo
                </span>
                <span className="block text-[12px] text-slate-500" data-id="element-4156" data-mp="44kp99tlchik">
                  Talab picks colours that suit your brand
                </span>
              </span>
            </button>}
        </div>

        <ColorRow label="Main colour" hint="Buttons and key actions" value={config.theme.primary} onChange={primary => setTheme({
        primary
      })} data-id="element-4157" data-mp="3raljjr2zintz" />
        <ColorRow label="Highlight colour" hint="Badges and small touches" value={config.theme.accent} onChange={accent => setTheme({
        accent
      })} data-id="element-4158" data-mp="2fvezgf6wf1a2" />
        {isLowContrastForWhite(config.theme.primary) && <p className="text-[12px] text-slate-500 leading-relaxed" data-id="element-4159" data-mp="le53crtlutfk">
            That’s a light colour, so button text switches to dark automatically
            to stay readable.
          </p>}

        <div data-id="element-4160" data-mp="cjqtx5vrad4d">
          <ControlLabel hint="Ready-made combinations using your colours" data-id="element-4161" data-mp="cka9wh1uirfr">
            Style
          </ControlLabel>
          <div className="flex flex-wrap gap-1.5" data-id="element-4162" data-mp="7jm7z0c2wq6r">
            {THEME_PRESETS.map(x => {
            const on = config.theme.preset === x.id;
            return <button key={x.id} onClick={() => applyPreset(x.id)} aria-pressed={on} className={`px-3 py-1.5 rounded-full text-[12.5px] font-medium transition-colors duration-150 ease-out ${on ? 'bg-dark-navy text-white' : 'bg-slate-100 text-slate-600 hover:bg-slate-200'}`} data-id="element-4163" data-mp="3ttou9k6szzrg">
                  {x.name}
                </button>;
          })}
          </div>
        </div>
      </DesignSection>

      {/* 3 — Background */}
      <DesignSection icon={LayersIcon} title="Background" summary={backdropName} open={open === 'background'} onToggle={() => toggle('background')} data-id="element-4164" data-mp="3aukap53ed90y">
        <div data-id="element-4165" data-mp="3jr30w9k8uitu">
          <ControlLabel data-id="element-4166" data-mp="3jqt9chjjw3m1">Pattern</ControlLabel>
          <VisualChoice<BackdropStyle> columns={5} value={config.theme.backdrop} onChange={backdrop => setTheme({
          backdrop
        })} options={BACKDROPS.map(b => ({
          id: b.id,
          label: b.label,
          preview: <span className="block h-11" style={{
            background: b.thumb(p)
          }} data-id="element-4168" data-mp="30z0fym0kkgds" />
        }))} data-id="element-4167" data-mp="2mzrb2ihtd8p4" />
        </div>
        <div data-id="element-4169" data-mp="b8596de15bcm">
          <ControlLabel data-id="element-4170" data-mp="b8dp6e5g8ws1">Colour</ControlLabel>
          <VisualChoice<BackgroundStyle> columns={4} value={config.theme.backgroundStyle} onChange={backgroundStyle => setTheme({
          backgroundStyle
        })} options={[{
          id: 'clean',
          label: 'White',
          preview: <span className="block h-9 bg-white" data-id="element-4172" data-mp="9tb57xlpfjky" />
        }, {
          id: 'gradient',
          label: 'Fade',
          preview: <span className="block h-9" style={{
            background: `linear-gradient(180deg, ${rgba(p, 0.16)}, #fff)`
          }} data-id="element-4173" data-mp="jpo02yszk3ha" />
        }, {
          id: 'tint',
          label: 'Tint',
          preview: <span className="block h-9" style={{
            background: rgba(p, 0.1)
          }} data-id="element-4174" data-mp="17g9uvodzbr9c" />
        }, {
          id: 'custom',
          label: 'Custom',
          preview: <span className="block h-9" style={{
            background: config.theme.background
          }} data-id="element-4175" data-mp="1gfoo2vlj6fxj" />
        }]} data-id="element-4171" data-mp="g89pa2b1042b" />
        </div>
        {config.theme.backgroundStyle === 'custom' && <ColorRow label="Background colour" value={config.theme.background} onChange={background => setTheme({
        background
      })} data-id="element-4176" data-mp="1v4ggbp8bl0cc" />}
        {config.theme.backdrop !== 'plain' && <div data-id="element-4177" data-mp="1m4te517rnqi8">
            <ControlLabel data-id="element-4178" data-mp="1q3sk8p58htb9">Pattern strength</ControlLabel>
            <RangeControl value={config.theme.backgroundStrength} onChange={backgroundStrength => setTheme({
          backgroundStrength
        })} low="Subtle" high="Strong" label="Pattern strength" data-id="element-4179" data-mp="1q292eu3xja7d" />
          </div>}
      </DesignSection>

      {/* 4 — Welcome text */}
      <DesignSection icon={TypeIcon} title="Welcome text" summary={config.hero.headline || 'Add a headline'} open={open === 'welcome'} onToggle={() => toggle('welcome')} data-id="element-4180" data-mp="17d1ctt4t4czi">
        <div className="space-y-4" data-id="element-4181" data-mp="ybusta2y8k1i">
          <div className="flex justify-end -mb-2" data-id="element-4182" data-mp="yc3adfa0odj1">
            <AiButton onClick={generateHeroCopy} label="Write it for me" busy={writing} data-id="element-4183" data-mp="302brdzawsrsc" />
          </div>
          <Field label="Headline" value={config.hero.headline} onChange={headline => setHero({
          headline
        })} data-id="element-4184" data-mp="2vb3lhd8sfojx" />
          <TextArea label="Short description" rows={3} value={config.hero.subtitle} onChange={subtitle => setHero({
          subtitle
        })} data-id="element-4185" data-mp="2q9w88ynvlx5v" />
        </div>

        <div data-id="element-4186" data-mp="3e1tlmu6z4x93">
          <ControlLabel hint={`Styles the last word, “${lastWord}”, in your highlight colour`} data-id="element-4187" data-mp="3e233tvls2048">
            Make the last word stand out
          </ControlLabel>
          <VisualChoice<HeroEmphasis> columns={4} value={config.hero.emphasis} onChange={emphasis => setHero({
          emphasis
        })} options={([['none', 'None', {}], ['underline', 'Underline', {
          boxShadow: `inset 0 -3px 0 ${config.theme.accent}`
        }], ['highlight', 'Highlight', {
          background: rgba(config.theme.accent, 0.45)
        }], ['colour', 'Colour', {
          color: config.theme.accent
        }]] as [HeroEmphasis, string, React.CSSProperties][]).map(([id, label, style]) => ({
          id,
          label,
          preview: <span className="flex h-11 items-center justify-center bg-slate-50" data-id="element-4189" data-mp="mwwyjofdlz8j">
                  <span className="font-display font-bold text-[15px] text-dark-navy px-0.5" style={style} data-id="element-4190" data-mp="rtnembr6kuty">
                    Aa
                  </span>
                </span>
        }))} data-id="element-4188" data-mp="3a2cg60rjs03x" />
          {(config.hero.emphasis === 'underline' || config.hero.emphasis === 'highlight') && <div className="mt-4" data-id="element-4191" data-mp="15toim02qsidn">
              <RangeControl value={config.hero.emphasisWeight} onChange={emphasisWeight => setHero({
            emphasisWeight
          })} low="Thin" high="Thick" label="Emphasis thickness" data-id="element-4192" data-mp="33jzj7tvxro0v" />
            </div>}
        </div>

        <div data-id="element-4193" data-mp="2jqhlf4scpfgg">
          <button onClick={() => setMoreWelcome(m => !m)} aria-expanded={moreWelcome} className="inline-flex items-center gap-1 text-[12.5px] font-semibold text-slate-500 hover:text-primary-indigo transition-colors duration-150 ease-out" data-id="element-4194" data-mp="2jr17mxp1aq0a">
            <ChevronDownIcon className={`w-3.5 h-3.5 transition-transform duration-150 ease-out ${moreWelcome ? 'rotate-180' : ''}`} data-id="element-4195" data-mp="25pyha8r0atjk" />
            Top-of-page style
          </button>
          {moreWelcome && <div className="mt-3" data-id="element-4196" data-mp="2espxsmyqofi2">
              <VisualChoice<HeroStyle> columns={3} value={config.hero.style} onChange={style => setHero({
            style
          })} options={[{
            id: 'minimal',
            label: 'Minimal',
            preview: <span className="block h-10 bg-white" data-id="element-4198" data-mp="3to91nhfdj4dl" />
          }, {
            id: 'brand',
            label: 'Brand',
            preview: <span className="block h-10" style={{
              background: rgba(p, 0.12)
            }} data-id="element-4199" data-mp="1ic638oo3kqb" />
          }, {
            id: 'image',
            label: 'Photo',
            preview: <span className="flex h-10 items-center justify-center bg-slate-200" data-id="element-4200" data-mp="g9l68bq74ohp">
                        <ImageIcon className="w-4 h-4 text-slate-400" data-id="element-4201" data-mp="l865a7tpp24k" />
                      </span>
          }]} data-id="element-4197" data-mp="29s1vv68z2e92" />
            </div>}
        </div>
      </DesignSection>

      {/* 5 — Buttons & chat */}
      <DesignSection icon={MousePointerClickIcon} title="Buttons & chat" summary={`${shapeName} buttons`} open={open === 'buttons'} onToggle={() => toggle('buttons')} data-id="element-4202" data-mp="bgrwf9fvepyr">
        <div data-id="element-4203" data-mp="140qlaoobbcqy">
          <ControlLabel data-id="element-4204" data-mp="2vk9vy1i2qz8h">Button shape</ControlLabel>
          <VisualChoice<ButtonShape> columns={3} value={config.theme.buttonShape} onChange={buttonShape => setTheme({
          buttonShape
        })} options={(['rounded', 'soft', 'square'] as ButtonShape[]).map(id => ({
          id,
          label: {
            rounded: 'Rounded',
            soft: 'Soft',
            square: 'Square'
          }[id],
          preview: <span className="flex h-12 items-center justify-center bg-slate-50" data-id="element-4206" data-mp="2bv970db76ph4">
                  <span className="px-3.5 py-1.5 text-[11px] font-semibold" style={{
              background: t.primary,
              color: t.primaryText,
              borderRadius: SHAPE_RADIUS[id]
            }} data-id="element-4207" data-mp="2gvdmq14a7g6a">
                    Ask
                  </span>
                </span>
        }))} data-id="element-4205" data-mp="2vkjl5mvap2d4" />
        </div>

        <div data-id="element-4208" data-mp="11wpd858zznx">
          <ControlLabel data-id="element-4209" data-mp="632e6uvsf2s6">Chat box background</ControlLabel>
          <VisualChoice<ChatBackground> columns={2} value={config.theme.chatBackground} onChange={chatBackground => setTheme({
          chatBackground
        })} options={[{
          id: 'white',
          label: 'White',
          preview: <span className="block h-9 bg-white" data-id="element-4211" data-mp="oz2pa0ep4bm7" />
        }, {
          id: 'tint',
          label: 'Tinted',
          preview: <span className="block h-9" style={{
            background: rgba(p, 0.08)
          }} data-id="element-4212" data-mp="k71fcl502wzc" />
        }]} data-id="element-4210" data-mp="62kchiqcvwcv" />
        </div>

        <SwitchRow title="Match chat to my colours" desc="Recommended. Turn off to pick separate chat colours." checked={config.theme.useBrandAutomatically} onChange={useBrandAutomatically => setTheme({
        useBrandAutomatically
      })} data-id="element-4213" data-mp="yzblhe8otx1l" />
        {!config.theme.useBrandAutomatically && <div className="space-y-5 pl-4 border-l-2 border-slate-100" data-id="element-4214" data-mp="39os267vu9nc9">
            <ColorRow label="Send button" value={config.theme.chatButtonColor || config.theme.primary} onChange={chatButtonColor => setTheme({
          chatButtonColor
        })} data-id="element-4215" data-mp="35nyffhu4ueky" />
            <ColorRow label="Chat highlights" value={config.theme.chatAccentColor || config.theme.accent} onChange={chatAccentColor => setTheme({
          chatAccentColor
        })} data-id="element-4216" data-mp="1dlvriv7z2wip" />
          </div>}
      </DesignSection>

      {/* 6 — Top bar */}
      <DesignSection icon={PanelTopIcon} title="Top bar" summary={[config.showTalabBranding ? 'Talab mark' : 'No Talab mark', config.showCountrySelector ? 'country picker' : 'no country picker'].join(' · ')} open={open === 'topbar'} onToggle={() => toggle('topbar')} data-id="element-4217" data-mp="1rmq1hiao55jh">
        <SwitchRow title="Show the Talab mark" desc={canRemoveTalab ? 'Included in your Scale plan.' : 'Try it off to preview — keeping it off needs the Scale plan.'} checked={config.showTalabBranding} onChange={showTalabBranding => update({
        showTalabBranding
      })} data-id="element-4218" data-mp="26ab1328c4mmn" />
        {!config.showTalabBranding && !canRemoveTalab && <div className="flex items-center gap-3 p-3 rounded-xl bg-warning/[0.07] ring-1 ring-warning/20" data-id="element-4219" data-mp="3qt3pggyh018e">
            <LockIcon className="w-4 h-4 text-warning flex-shrink-0" data-id="element-4220" data-mp="3lua8ufnvzu40" />
            <p className="flex-1 text-[12px] text-slate-600" data-id="element-4221" data-mp="3lw4yg7a3saef">Preview only on your plan.</p>
            <button type="button" onClick={() => setUpgrade(true)} className="px-3 py-1.5 rounded-lg bg-primary-indigo text-white text-[12px] font-semibold hover:bg-indigo-700 transition-colors duration-150 ease-out" data-id="element-4222" data-mp="nblg2531um5v">
              Upgrade
            </button>
          </div>}
        <SwitchRow title="Show the country picker" desc="Useful if you serve more than one country." checked={config.showCountrySelector} onChange={showCountrySelector => update({
        showCountrySelector
      })} data-id="element-4223" data-mp="162mhr0vk0vt4" />
        <p className="text-[11.5px] text-slate-400" data-id="element-4224" data-mp="lsv13d1hyybm">
          “Powered by Talab.app” always stays in your footer.
        </p>
        {/* Prototype-only: flip plans to see both behaviours */}
        <div className="flex items-center gap-2 pt-3 border-t border-dashed border-slate-200" data-id="element-4225" data-mp="bxjn4wxwxv4x">
          <span className="text-[11px] text-slate-400" data-id="element-4226" data-mp="gvlc1yi0soob">Simulate plan</span>
          <div className="ml-auto inline-flex p-0.5 bg-slate-100 rounded-lg" data-id="element-4227" data-mp="14lp4d9prd200">
            {['Growth', 'Scale'].map(plan => <button key={plan} type="button" onClick={() => subscribePackage(plan)} className={`px-2.5 py-1 rounded-md text-[11px] font-semibold transition-colors duration-150 ease-out ${currentPlanId === plan ? 'bg-white text-dark-navy shadow-sm' : 'text-slate-500 hover:text-slate-700'}`} data-id="element-4228" data-mp="19ixovgydpi4f">
                {plan}
              </button>)}
          </div>
        </div>
      </DesignSection>

      {upgrade && <UpgradePlanModal initialPlan="Scale" onClose={() => setUpgrade(false)} data-id="element-4229" data-mp="3cz2lq0vy67uu" />}

      {/* Quiet escape hatch, well away from everyday controls */}
      <div className="pt-3 flex justify-center" data-id="element-4230" data-mp="3lspf5kcomz0v">
        <button onClick={onResetTheme} className="inline-flex items-center gap-1.5 text-[12.5px] text-slate-400 hover:text-dark-navy transition-colors duration-150 ease-out" data-id="element-4231" data-mp="3ls5ys82okioy">
          <RotateCcwIcon className="w-3.5 h-3.5" data-id="element-4232" data-mp="2u21hiia7wbry" />
          Reset colours to my brand
        </button>
      </div>
    </div>;
}